import { CampaignType_Enum as CampaignType, Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { Button, FileUpload } from '@frontend/design-system';
import { TrayMode } from '../../types';
import { downloadExampleCsv } from './download-example-csv';
import { UploadStep } from './upload-step';
import { useCsvRecipientsParse } from './use-csv-recipients-parse';

type Props = {
  campaignId: string;
  campaignType: CampaignType;
  locationId: string;
  setMode: (mode: TrayMode) => void;
  setRecipientsToUpload: (recipients: Recipient[]) => void;
  trackingIdPrefix: string;
};

export const UploadCSVModal = ({
  campaignId,
  campaignType,
  locationId,
  setMode,
  setRecipientsToUpload,
  trackingIdPrefix,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const parseRecipientsFromCsv = useCsvRecipientsParse(locationId, campaignId, campaignType);

  const handleFileUpload = async (files: File[]) => {
    const result = await parseRecipientsFromCsv(files);
    if (result.error) return;
    setRecipientsToUpload(result.recipients);
    setMode('display');
  };

  const handleFileValidation = async (file: File) => {
    const result = await parseRecipientsFromCsv([file]);
    return result.error;
  };

  return (
    <>
      <UploadStep title={t('Step 1:')} instructions={t('Download File.')} details={t('Download the example CSV file.')}>
        <Button
          variant='secondary'
          onClick={() => downloadExampleCsv(campaignType)}
          trackingId={`${trackingIdPrefix}-contacts-upload-download-btn`}
          size='large'
        >
          {t('Download Example CSV')}
        </Button>
      </UploadStep>
      <UploadStep
        title={t('Step 2:')}
        instructions={t('Copy & Paste Contacts Into Downloaded File.')}
        details={t('Uploaded lists have no count restrictions.')}
      />
      <UploadStep
        title={t('Step 3:')}
        instructions={t('Upload List Below.')}
        details={
          campaignType === CampaignType.EMAIL
            ? t('Unsubscribed email addresses will not receive an email.')
            : t('Unsubscribed phone numbers will not receive a text.')
        }
      >
        <FileUpload
          trackingId={`${trackingIdPrefix}-contacts-csv-file-upload`}
          helperText={t('Drag & drop CSV or select from your computer')}
          multiple={false}
          onFileUpload={handleFileUpload}
          acceptedFileType='csv'
          keepPreviouslySelected={false}
          validateUpload={handleFileValidation}
        />
      </UploadStep>
    </>
  );
};
